import {Alert, Box, Checkbox} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import * as _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {toDisplaySpeed} from '../../../constants/common';
import {useAppSelector} from '../../../hooks/redux';
import usePrevious from '../../../hooks/usePrevious';
import {
  HazardAIMachineSummary,
  HazardAIMachineSummaryQuery,
  HazardAIMachineSummaryResponse,
} from '../../../interfaces/HazardAIMachineSummary';
import reduxSelectors from '../../../redux/selectors';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {usePanelFilter} from '../../dashboards/entities/DashboardEntityContext';

export interface HazardAISummaryGridConfig {
  selectedIds?: string[];
  shownFields?: string[];
  grid?: {
    limit?: number;
    page?: number;
  };
  params?: HazardAIMachineSummaryQuery;
  columnWidths: Record<string, number>;
  columnOrder?: string[];
}

export interface HazardAISummaryGridProps {
  value?: HazardAISummaryGridConfig;
  onChange?: (value: HazardAISummaryGridConfig) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'safeye_node_id',
  'machine_name',
  'site_name',
  'machine_type',
  'hazard_person',
  'warning_person',
  'id_person',
  'hazard_vehicle',
  'warning_vehicle',
  'id_vehicle',
  'max_speed',
  'operation_time',
  'operating_no_movement',
  'idle_time',
];

const HazardAISummaryGrid = ({value, onChange}: HazardAISummaryGridProps) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);
  const speedFormat = useAppSelector(({app}) => app.speedFormat);
  const {filter} = usePanelFilter();
  const dataGridRef = useRef<DataGridRef>(null);

  const [fetchedData, setFetchedData] =
    useState<HazardAIMachineSummaryResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const defaultConfig = {
    selectedIds: [],
    shownFields: DEFAULT_SHOWN_FIELDS,
    grid: {
      page: 0,
      limit: 25,
    },
    params: value?.params || {},
    columnWidths: {},
    columnOrder: undefined,
  };
  const [config, setConfig] = useState<HazardAISummaryGridConfig>({
    ...defaultConfig,
    ...value,
  });

  const params = useMemo<HazardAIMachineSummaryQuery>(
    () => ({
      date_start:
        filter.params?.date_start ??
        config.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        filter.params?.date_end ??
        config.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      safeye_node_id: _.has(filter.params, 'safeye_node_ids')
        ? filter.params?.safeye_node_ids
        : config?.params?.safeye_node_id,
      site_id: _.has(filter.params, 'site_ids')
        ? filter.params?.site_ids
        : config?.params?.site_id,
      type_id: _.has(filter.params, 'type_ids')
        ? (filter.params?.type_ids as number[] | null)
        : config?.params?.type_id != null
          ? [config.params.type_id].flat()
          : null,
      limit: config.grid?.limit,
      page: config.grid?.page,
    }),
    [filter, config]
  );

  const fetchData = useCallback(
    async (params: HazardAIMachineSummaryQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/hazard-ai/machine-summary-grid`;
        const resp = await API.get<HazardAIMachineSummaryResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_462114e5');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  /*************/
  /* data grid */
  /*************/

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params, config.grid]);

  const rows = fetchedData?.items || [];

  const cols: DataGridColumn<HazardAIMachineSummary>[] = [
    {
      field: 'select',
      type: 'select',
      width: 40,
      doNotExport: true,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.safeye_node_id)}
          onChange={() => toggleSelectItem(row.safeye_node_id)}
        />
      ),
    },
    {
      field: 'safeye_node_id',
      headerName: 'Node ID',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'machine_name',
      headerName: 'Machine Name',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'site_name',
      headerName: 'Site',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'machine_type',
      headerName: 'Machine Type',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'hazard_person',
      headerName: 'Hazard Person',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'warning_person',
      headerName: 'Warning Person',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'id_person',
      headerName: 'ID Person',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'hazard_vehicle',
      headerName: 'Hazard Vehicle',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'warning_vehicle',
      headerName: 'Warning Vehicle',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'id_vehicle',
      headerName: 'ID Vehicle',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'hazard_object',
      headerName: 'Hazard Object',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'warning_object',
      headerName: 'Warning Object',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'id_object',
      headerName: 'ID Object',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'max_speed',
      headerName: `Max Speed (${speedFormat})`,
      width: 100,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) =>
        row?.max_speed != null
          ? toDisplaySpeed(Number(row.max_speed), speedFormat)
          : null,
    },
    {
      field: 'operation_time',
      headerName: 'Operation Time',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'operating_no_movement',
      headerName: 'Operating with No Movement',
      width: 200,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'idle_time',
      headerName: 'Idle Time',
      width: 100,
      resizeable: true,
      sortable: true,
    },
  ];

  const shownFields = useMemo(() => {
    return config.shownFields;
  }, [config]);

  const handleChangeShownFields = (fields: string[]) => {
    const updated = update(config, {
      shownFields: {$set: fields},
    });
    setConfig(updated);
    onChange?.(updated);
  };

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = config.selectedIds || [];

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedItems.includes(i.safeye_node_id)),
    [rows, config.selectedIds]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const toggleSelectItem = (id: string) => {
    const selectedIds = selectedItems.includes(id)
      ? config.selectedIds?.filter((i) => i !== id)
      : [...(config.selectedIds || []), id];
    const updated = update(config, {
      selectedIds: {
        $set: selectedIds,
      },
    });
    setConfig(updated);
    onChange?.(updated);
  };

  const selectAll = () => {
    const updated = update(config, {
      selectedIds: {
        $set: rows?.map((i) => i.safeye_node_id) || [],
      },
    });
    setConfig(updated);
    onChange?.(updated);
  };

  const unselectAll = () => {
    const updated = update(config, {
      selectedIds: {
        $set: [],
      },
    });
    setConfig(updated);
    onChange?.(updated);
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  useEffect(() => {
    if (selectedItems && selectedItems.length !== selectedRows.length) {
      const updated = update(config, {
        selectedIds: {
          $set: selectedRows.map((i) => i.safeye_node_id),
        },
      });
      setConfig(updated);
      onChange?.(updated);
    }
  }, [config.selectedIds, selectedRows]);

  const prevSelectedAll = usePrevious(selectedAll);

  useEffect(() => {
    if (prevSelectedAll && !selectedAll) {
      selectAll();
    }
  }, [rows]);

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    const updated = update(config, {
      columnWidths: {$set: widths},
    });
    setConfig(updated);
    onChange?.(updated);
  };

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      const updated = update(config, {
        columnOrder: {$set: order},
        shownFields: {$set: fields},
      });
      setConfig(updated);
      onChange?.(updated);
    },
    [onChange, config]
  );

  return (
    <Box height="50%" bgcolor={isDarkMode ? 'background.default' : 'grey.100'}>
      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      {/* Chart */}
      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={cols}
        size="small"
        pagination
        paginationMode="server"
        page={config.grid?.page}
        pageSize={config.grid?.limit}
        rowCount={fetchedData?.count}
        loading={fetchedInProgress}
        shownFields={shownFields}
        defaultShownFields={DEFAULT_SHOWN_FIELDS}
        columnWidths={config?.columnWidths}
        columnOrder={config?.columnOrder}
        sxFooter={{
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
        }}
        footerStart={
          <Box display="flex" alignItems="center" gap={3} px={1}>
            <Box
              display="flex"
              gap={0.5}
              alignItems="center"
              height="100%"
              whiteSpace="nowrap"
            >
              {selectedItems.length} selected
            </Box>
          </Box>
        }
        onShownFieldsChange={handleChangeShownFields}
        onColumnWidthsChange={handleColumnWidthChange}
        onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
        onPageChange={(v) => {
          const updated = update(config, {
            grid: {
              page: {
                $set: v,
              },
            },
          });
          setConfig(updated);
          onChange?.(updated);
        }}
        onPageSizeChange={(v) => {
          const updated = update(config, {
            grid: {
              limit: {
                $set: v,
              },
            },
          });
          setConfig(updated);
          onChange?.(updated);
        }}
      />
    </Box>
  );
};

export default HazardAISummaryGrid;
